import {
  ReactElement,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useState,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import Debug from "debug";
import { ScreenCloud, ScreenCloudContext } from "../../ScreenCloudReactApp";
import "./Player.scss";
import StreamPlayer, {
  StreamPlayerType,
} from "../../components/StreamPlayer/StreamPlayer";
import { Loader } from "@screencloud/screencloud-ui-components";
import { ErrorState, StreamState, TokenStore, ViewerStore } from "../../types";
import {
  receivedStudioToken,
  failedStudioToken,
  endViewerStreamStatePolling,
  getViewerBroadcastSettings,
  getViewerPlaybackUrl,
  startViewerStreamStatePolling,
  getViewerChannel,
  setViewerStreamState,
} from "../../actions";
import { RootState } from "../../reducers";
import StreamOffline from "../../components/StreamOffline/StreamOffline";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import { getErrorObj } from "../../utils/errorUtils";
import { AUTH_TOKEN_DELAY, MAX_AUTH_TOKEN_ATTEMPTS } from "../../constants";

const debug = Debug("broadcast:containers:Player");

interface PlayerProps {
  sc: ScreenCloud;
}

const Player = (props: PlayerProps): ReactElement<PlayerProps> => {
  const dispatch = useDispatch();

  const { sc } = props;
  const { orgId, spaceId = "", config, requestAuthToken } = sc;
  const { channelId } = config;
  const authTokenAttempts = useRef(0);
  const authTokenTimeout = useRef(0);
  const [isTokenRetryConsumed, setIsTokenRetryConsumed] = useState(false);

  const studioToken: TokenStore = useSelector(
    (state: RootState) => state.studio
  );
  const viewer: ViewerStore = useSelector((state: RootState) => state.viewer);
  const { logger } = useContext(ScreenCloudContext);

  const clearRetryTimeout = () => {
    if (authTokenTimeout.current) {
      window.clearTimeout(authTokenTimeout.current);
    }
  };

  const getAuthToken = useCallback(async (): Promise<string | undefined> => {
    do {
      clearRetryTimeout();
      try {
        authTokenAttempts.current++;
        const { authToken } = await requestAuthToken();
        return authToken;
      } catch (err) {
        logger?.error(`Request auth token failed ${err}`, {
          authTokenAttempts: authTokenAttempts.current,
        });
        if (authTokenAttempts.current === MAX_AUTH_TOKEN_ATTEMPTS) {
          logger?.error(
            `Request auth token failed, Auth token retries exhausted. ${err}`,
            {
              maxAuthTokenAttempts: MAX_AUTH_TOKEN_ATTEMPTS,
            }
          );
          throw err;
        }

        await wait(AUTH_TOKEN_DELAY);
      }
    } while (authTokenAttempts.current < MAX_AUTH_TOKEN_ATTEMPTS);
  }, [requestAuthToken, logger]);

  const wait = (ms: number): Promise<undefined> => {
    return new Promise(
      (resolve, reject) =>
        (authTokenTimeout.current = window.setTimeout(resolve, ms))
    );
  };

  useEffect(() => {
    if (channelId) {
      const getInfo = async (): Promise<void> => {
        try {
          const authToken = await getAuthToken();
          authToken && dispatch(receivedStudioToken(authToken));
          logger?.info("requestAuthToken success");
        } catch (error) {
          logger?.error("requestAuthToken failed", getErrorObj(error));
          dispatch(failedStudioToken(error));
          setIsTokenRetryConsumed(true);
        }
      };

      getInfo();
    }

    return () => {
      dispatch(endViewerStreamStatePolling());
      clearRetryTimeout();
    };
  }, [dispatch, channelId, requestAuthToken, logger, getAuthToken]);

  useEffect(() => {
    debug("token updated");
    const { token } = studioToken;
    if (token !== null) {
      if (config && config.channelId) {
        const { channelId } = config;
        dispatch(getViewerChannel({ orgId, spaceId, channelId }, logger));
      }
    }
  }, [studioToken, dispatch, orgId, spaceId, channelId, config, logger]);

  useEffect(() => {
    if (viewer.channel !== null && studioToken.token !== null) {
      if (
        viewer.channel.ingestEndpoint === undefined &&
        viewer.channel.streamKey === undefined
      ) {
        dispatch(
          getViewerBroadcastSettings({
            orgId: viewer.channel.screenCloudOrgId,
            spaceId: viewer.channel.spaceId,
            channelId: viewer.channel.channelId,
          })
        );
        dispatch(
          getViewerPlaybackUrl({
            orgId: viewer.channel.screenCloudOrgId,
            spaceId: viewer.channel.spaceId,
            channelId: viewer.channel.channelId,
          })
        );
      }
    }
  }, [viewer, dispatch, studioToken.token]);

  useEffect(() => {
    if (viewer.channel !== null) {
      const { screenCloudOrgId, spaceId, channelId } = viewer.channel;

      if (viewer.streamState === StreamState.Unknown) {
        dispatch(
          startViewerStreamStatePolling({
            orgId: screenCloudOrgId,
            spaceId,
            channelId,
          })
        );
      }
    }
  }, [viewer.streamState, viewer.channel, dispatch]);

  if (viewer.loading) {
    return (
      <div className="loader-container">
        <Loader inline="centered" active size="massive" inverted />
      </div>
    );
  }

  return (
    <div className="live-streams-player">
      <div className="viewer-error">
        {viewer.errors.length > 0 &&
          viewer.errors.map((error) =>
            error.error !== ErrorState.FailedToVerifyPassword ? (
              <ErrorMessage key={error.uuid} error={error} />
            ) : (
              <div />
            )
          )}
      </div>
      {studioToken.token === null ? (
        isTokenRetryConsumed ? (
          <>
            There was an error fetching this stream. Please refresh your player
            to try again.
            <br /> If the problem persists, please check your stream
            configuration on ScreenCloud Studio.
          </>
        ) : (
          <Loader size="massive" active inline="centered" inverted />
        )
      ) : viewer.channel !== null &&
        viewer.channel.playbackUrl !== undefined ? (
        viewer.streamState === StreamState.Live ? (
          <StreamPlayer
            url={viewer.channel.playbackUrl}
            type={StreamPlayerType.IVS}
            onStreamStateChange={(state) =>
              dispatch(setViewerStreamState(state))
            }
          />
        ) : (
          <StreamOffline state={viewer.streamState} />
        )
      ) : (
        <Loader size="massive" active inline="centered" inverted />
      )}
    </div>
  );
};

export default Player;
